import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button';
import { Input } from './input';
import { Checkbox } from './checkbox';
import { Badge } from './badge';
import { Search, ChevronDown, X } from 'lucide-react';

/**
 * Dropdown multi-selezione con search interna.
 *
 * Props:
 * - items: [{ id, label }] o [{ id, name }]
 * - selectedIds: string[]
 * - onChange: (newIds) => void
 * - placeholder: string (etichetta quando niente selezionato)
 * - labelKey: 'label' | 'name' (default 'label')
 * - width: className width (es. 'w-[200px]')
 * - testId: base data-testid
 */
export function MultiSelectPopover({
  items = [],
  selectedIds = [],
  onChange,
  placeholder = 'Seleziona…',
  labelKey = 'label',
  width = 'w-[220px]',
  testId = 'multi-select',
}) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter((it) => String(it[labelKey] || it.name || '').toLowerCase().includes(q));
  }, [items, search, labelKey]);

  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };
  const clearAll = () => onChange([]);

  const selectedLabels = items
    .filter((it) => selectedIds.includes(it.id))
    .map((it) => it[labelKey] || it.name || it.id);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`${width} justify-between font-normal`}
          size="sm"
          data-testid={`${testId}-trigger`}
        >
          <span className="truncate text-left flex-1">
            {selectedIds.length === 0 ? (
              <span className="text-gray-500">{placeholder}</span>
            ) : selectedIds.length === 1 ? (
              selectedLabels[0]
            ) : (
              <span>
                {selectedLabels[0]} <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">+{selectedIds.length - 1}</Badge>
              </span>
            )}
          </span>
          <ChevronDown className="w-4 h-4 opacity-50 flex-shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-[260px]" align="start" data-testid={`${testId}-content`}>
        <div className="p-2 border-b">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca…"
              className="h-8 pl-7 text-sm"
              data-testid={`${testId}-search`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto py-1">
          {filtered.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-gray-400">Nessun risultato</p>
          ) : (
            filtered.map((it) => {
              const label = it[labelKey] || it.name || it.id;
              return (
                <label
                  key={it.id}
                  className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 cursor-pointer text-sm"
                  data-testid={`${testId}-option-${it.id}`}
                >
                  <Checkbox
                    checked={selectedIds.includes(it.id)}
                    onCheckedChange={() => toggle(it.id)}
                  />
                  <span className="line-clamp-1">{label}</span>
                </label>
              );
            })
          )}
        </div>
        {selectedIds.length > 0 && (
          <div className="border-t px-2 py-1.5 flex items-center justify-between">
            <span className="text-[11px] text-gray-500">{selectedIds.length} selezionati</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] text-red-600 hover:text-red-800 flex items-center gap-1"
              data-testid={`${testId}-clear`}
            >
              <X className="w-3 h-3" /> Deseleziona
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
